import { FunctionComponent, useCallback, useEffect, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import { CampaignRoutingCondition } from "@signalist/shared-types";

import { MultiAutoComplete } from "@/components/MultiAutoComplete/MultiAutoComplete";
import {
	CompanyEnrichAutoComplete,
	CompanyEnrichAutoCompleteItem,
} from "@/pages/Private/pages/CompanyEnrich/components/CompanyEnrichAutoComplete";
import { useLazyGetFilterByPropertyQuery } from "@/pages/Private/redux/list/list.api";

import { ROUTING_I18N, valueSourceOf } from "./utils/campaignRoutingFields";
import { ROUTING_LANGUAGE_OPTIONS, routingLanguageOptionOf } from "./utils/routingLanguages";
import { RoutingRangeValues } from "./RoutingRangeValues";
import { RoutingTextValues } from "./RoutingTextValues";

interface RoutingValuePickerProps {
	listId: number;
	condition: CampaignRoutingCondition;
	onChange: (update: Partial<CampaignRoutingCondition>) => void;
}

type Option = { id: string; title: string };

const toOptions = (rows: { value: string }[] | undefined): Option[] =>
	(rows ?? []).filter((row) => row.value).map((row) => ({ id: row.value, title: row.value }));

/**
 * A short fixed list from the lead-table filter lookup (segments, priority, seniority), loaded once
 * when the condition shows it. A failed load says so: an empty list would read as "no values".
 */
const EagerLookupValues: FunctionComponent<{
	listId: number;
	property: string;
	selected: Option[];
	onChange: (values: string[]) => void;
}> = ({ listId, property, selected, onChange }) => {
	const { t } = useTranslation();
	const [getOptions] = useLazyGetFilterByPropertyQuery();
	const [answer, setAnswer] = useState<{ property: string; options: Option[] } | null>(null);
	const [failed, setFailed] = useState(false);

	useEffect(() => {
		let current = true;

		getOptions({ propertyName: property, searchTerm: "", listId })
			.unwrap()
			.then((result) => current && setAnswer({ property, options: toOptions(result?.data) }))
			.catch(() => current && setFailed(true));

		return () => {
			current = false;
		};
	}, [getOptions, listId, property]);

	if (failed) {
		return <p className="text-xs text-status-error">{t(`${ROUTING_I18N}.editor.valuesFailed`)}</p>;
	}

	return (
		<MultiAutoComplete
			data={answer?.property === property ? answer.options : []}
			handleSelect={(items) => onChange((items ?? []).map((item) => String(item.id)))}
			placeholder={t(`${ROUTING_I18N}.editor.selectValues`)}
			selectedItems={selected}
			stayOn
		/>
	);
};

/**
 * A long list from the lead-table filter lookup (countries, industries), searched as the user types.
 * A refused search never rejects into the autocomplete, which does not catch: it clears the stale
 * options and says the values could not be loaded, with a retry that runs the same search again.
 */
const SearchLookupValues: FunctionComponent<{
	listId: number;
	property: string;
	selected: Option[];
	onChange: (values: string[]) => void;
}> = ({ listId, property, selected, onChange }) => {
	const { t } = useTranslation();
	const [getOptions] = useLazyGetFilterByPropertyQuery();
	const [failed, setFailed] = useState(false);
	// A retry hands the autocomplete a new search function, so it runs its current query again.
	const [attempt, setAttempt] = useState(0);

	const searchOptions = useCallback(
		async (query: string): Promise<CompanyEnrichAutoCompleteItem[]> => {
			try {
				const result = await getOptions({
					propertyName: property,
					searchTerm: query,
					listId,
				}).unwrap();

				setFailed(false);

				return toOptions(result?.data);
			} catch {
				setFailed(true);

				return [];
			}
		},
		// `attempt` is not read inside: a retry only renews the function's identity.
		[getOptions, listId, property, attempt]
	);

	return (
		<div className="flex flex-col gap-1">
			<CompanyEnrichAutoComplete
				fetchFn={searchOptions}
				handleSelect={(items) => onChange((items ?? []).map((item) => item.id))}
				placeholder={t(`${ROUTING_I18N}.editor.searchValues`)}
				selectedItems={selected}
				stayOn
			/>
			{failed && (
				<p className="flex items-center gap-2 text-xs text-status-error" role="alert">
					{t(`${ROUTING_I18N}.editor.searchFailed`)}
					<button
						className="font-semibold underline"
						data-testid="routing-values-retry"
						type="button"
						onClick={() => setAttempt((current) => current + 1)}
					>
						{t(`${ROUTING_I18N}.editor.retry`)}
					</button>
				</p>
			)}
		</div>
	);
};

/** Picks a condition's values the way the lead table filters the same field. */
export const RoutingValuePicker: FunctionComponent<RoutingValuePickerProps> = ({
	listId,
	condition,
	onChange,
}) => {
	const { t } = useTranslation();
	const source = valueSourceOf(condition.field);
	const values = useMemo(() => condition.values ?? [], [condition.values]);
	const selected = useMemo(
		() =>
			source.kind === "language"
				? values.map(routingLanguageOptionOf)
				: values.map((value) => ({ id: value, title: value })),
		[source.kind, values]
	);
	const setValues = (next: string[]) => onChange({ values: next });

	if (condition.operator === "IS_EMPTY" || condition.operator === "IS_NOT_EMPTY") {
		return null;
	}

	switch (source.kind) {
		case "range":
			return (
				<RoutingRangeValues
					from={condition.from ?? null}
					fromLabel={t(`${ROUTING_I18N}.editor.from`)}
					to={condition.to ?? null}
					toLabel={t(`${ROUTING_I18N}.editor.to`)}
					onChange={onChange}
				/>
			);
		case "text":
			return (
				<RoutingTextValues
					placeholder={t(`${ROUTING_I18N}.editor.typeText`)}
					removeLabel={t(`${ROUTING_I18N}.editor.removeValue`)}
					values={values}
					onChange={setValues}
				/>
			);
		case "language":
			return (
				<MultiAutoComplete
					data={ROUTING_LANGUAGE_OPTIONS}
					handleSelect={(items) => setValues((items ?? []).map((item) => String(item.id)))}
					placeholder={t(`${ROUTING_I18N}.editor.selectValues`)}
					selectedItems={selected}
					stayOn
				/>
			);
		case "lookup":
			return source.eager ? (
				<EagerLookupValues
					key={source.property}
					listId={listId}
					property={source.property}
					selected={selected}
					onChange={setValues}
				/>
			) : (
				<SearchLookupValues
					key={source.property}
					listId={listId}
					property={source.property}
					selected={selected}
					onChange={setValues}
				/>
			);
		default: {
			const unknownSource: never = source;

			throw new Error(`Unknown value source ${JSON.stringify(unknownSource)}`);
		}
	}
};
